import { Node, mergeAttributes, canInsertNode, isNodeSelection } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import {
  POST_EDITOR_SPACER_SIZES,
  POST_EDITOR_SPACER_SIZE_KEYS,
  POST_EDITOR_SPACER_DEFAULT_SIZE,
  POST_EDITOR_SPACER_SIZE_LABELS,
  normalizePostSpacerSize,
} from '../lib/post_editor_constants.js';

// Invisible vertical spacer block. Used when an editor wants more breathing
// room than a block's built-in rhythm provides. Renders to a zero-content
// <div> with a fixed pixel height (see post_content_blocks.scss).

const sizeFromClassName = (className) => {
  if (typeof className !== 'string') return null;
  const m = className.match(/ps-spacer--([a-z]+)/i);
  return m ? m[1].toLowerCase() : null;
};

export const PostSpacer = Node.create({
  name: 'postSpacer',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  marks: '',

  addAttributes() {
    return {
      size: {
        default: POST_EDITOR_SPACER_DEFAULT_SIZE,
        parseHTML: (element) => {
          const dataAttr = element.getAttribute('data-ps-spacer-size');
          if (dataAttr) return normalizePostSpacerSize(dataAttr);
          const fromClass = sizeFromClassName(element.className);
          return fromClass ? normalizePostSpacerSize(fromClass) : POST_EDITOR_SPACER_DEFAULT_SIZE;
        },
        renderHTML: (attributes) => ({
          'data-ps-spacer-size': normalizePostSpacerSize(attributes.size),
        }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-ps-spacer-size]' },
      { tag: 'div.ps-spacer' },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const size = normalizePostSpacerSize(node.attrs.size);
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: `ps-spacer ps-spacer--${size}`,
        'data-ps-spacer-size': size,
        'aria-hidden': 'true',
      }),
    ];
  },

  addCommands() {
    return {
      // Insert a new spacer at the current selection (default size = M).
      setPostSpacer:
        (attrs = {}) =>
        ({ chain, state }) => {
          if (!canInsertNode(state, state.schema.nodes[this.name])) return false;
          const size = normalizePostSpacerSize(attrs.size);
          return chain()
            .insertContent({ type: this.name, attrs: { size } })
            .run();
        },

      // Update the size attribute of an existing spacer that is currently
      // selected as a NodeSelection (used by the picker popup when an editor
      // clicks on an existing spacer to resize it).
      updatePostSpacerSize:
        (size) =>
        ({ state, tr, dispatch }) => {
          const { selection } = state;
          if (!isNodeSelection(selection)) return false;
          if (selection.node?.type?.name !== this.name) return false;
          const newSize = normalizePostSpacerSize(size);
          if (selection.node.attrs.size === newSize) return true;
          if (dispatch) {
            tr.setNodeMarkup(selection.from, undefined, {
              ...selection.node.attrs,
              size: newSize,
            });
            // Re-select the (now updated) node so the picker stays anchored.
            tr.setSelection(NodeSelection.create(tr.doc, selection.from));
            dispatch(tr);
          }
          return true;
        },
    };
  },
});
